import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Package, User, ShoppingBag, Hammer, History, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/AuthImage";
import api, { fmt, fmtDate } from "@/lib/api";
import { priorityOf } from "./Products";

const Field = ({ label, value, cls }) => (
  <div>
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    <p className={`font-medium text-sm ${cls || ""}`}>{value ?? "—"}</p>
  </div>
);

export default function ProductDetail() {
  const { oid, pid } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["product", oid, pid],
    queryFn: async () => (await api.get(`/products/${oid}/${pid}`)).data,
  });

  if (isLoading || !data) return <div className="h-64 rounded-xl bg-muted animate-pulse" />;
  const { product: p, order: o, customer: c, payments, timeline } = data;
  const pr = priorityOf({ ...p, delivery_date: o.delivery_date });

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="back-button" onClick={() => navigate("/products")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {p.photo ? (
          <AuthImage src={`/orders/${o.id}/products/${p.id}/photo?v=${(p.photo.storage_path || "").slice(-8)}`} alt={p.name} className="h-11 w-11 rounded-xl object-cover border" />
        ) : (
          <div className="h-11 w-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Package className="h-5 w-5 text-amber-500" />
          </div>
        )}
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{p.name}</h1>
          <p className="text-sm text-muted-foreground">{p.category || "Uncategorised"} · Order {o.order_number}</p>
        </div>
        <StatusBadge status={p.status} />
        <Button data-testid="go-to-order-button" className="rounded-full active:scale-95 transition-transform" onClick={() => navigate(`/orders/${o.id}`)}>
          <ExternalLink className="h-4 w-4 mr-1.5" /> Go to Order
        </Button>
      </div>

      <div className="rounded-xl border bg-card p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-5" data-testid="product-summary-card">
        <Field label="Quantity" value={p.quantity} />
        <Field label="Customer Price" value={fmt(p.customer_price)} />
        <Field label="Fabric Cost" value={fmt(p.fabric_cost)} />
        <Field label="Stitching Cost" value={fmt(p.stitching_cost)} />
        <Field label="Karigar Cost" value={fmt(p.karigar_cost)} />
        <Field label="Profit" value={fmt(p.profit)} cls={p.profit >= 0 ? "text-emerald-600" : "text-red-500"} />
        <Field label="Total Cost" value={fmt(p.total_cost)} />
        <Field label="Priority" value={pr.label} cls={pr.cls} />
        <Field label="Sent Date" value={fmtDate(p.sent_date)} />
        <Field label="Expected Return" value={fmtDate(p.expected_return)} />
        <Field label="Received Date" value={fmtDate(p.received_date)} />
        <Field label="Location" value={p.location} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card" data-testid="product-customer-card">
          <div className="flex items-center gap-2 px-6 py-4 border-b">
            <User className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">Customer Information</h2>
          </div>
          <div className="p-6 grid grid-cols-2 gap-x-4 gap-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Name</p>
              <button data-testid="product-customer-link" className="font-medium text-sm text-amber-500 hover:underline" onClick={() => c.id && navigate(`/customers/${c.id}`)}>
                {c.name || "—"}
              </button>
            </div>
            <Field label="Phone" value={c.phone} />
            <Field label="Address" value={c.address} />
            <Field label="Notes" value={c.notes} />
          </div>
        </div>

        <div className="rounded-xl border bg-card" data-testid="product-order-card">
          <div className="flex items-center gap-2 px-6 py-4 border-b">
            <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">Order Information</h2>
          </div>
          <div className="p-6 grid grid-cols-2 gap-x-4 gap-y-4">
            <Field label="Order Number" value={o.order_number} />
            <Field label="Products in Order" value={o.products_count} />
            <Field label="Booking Date" value={fmtDate(o.order_date)} />
            <Field label="Delivery Date" value={fmtDate(o.delivery_date)} />
            <div><p className="text-xs text-muted-foreground mb-1">Order Status</p><StatusBadge status={o.status} /></div>
            <div><p className="text-xs text-muted-foreground mb-1">Payment Status</p><StatusBadge status={o.payment_status} /></div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card" data-testid="product-payment-card">
          <h2 className="font-heading font-semibold px-6 py-4 border-b">Payment & Profit Summary</h2>
          <div className="p-6 grid grid-cols-2 gap-x-4 gap-y-4">
            <Field label="Order Total" value={fmt(o.total_amount)} />
            <Field label="Paid" value={fmt(o.advance)} cls="text-green-600" />
            <Field label="Remaining" value={fmt(o.remaining)} cls={o.remaining > 0 ? "text-red-500" : ""} />
            <Field label="This Product Profit" value={fmt(p.profit)} cls="text-emerald-600" />
          </div>
          {payments.length > 0 && (
            <ul className="border-t divide-y">
              {payments.map((pm) => (
                <li key={pm.id} className="px-6 py-2.5 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{fmtDate(pm.date)}{pm.remarks && ` · ${pm.remarks}`}</span>
                  <span className="font-medium text-green-600">{fmt(pm.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border bg-card" data-testid="product-karigar-card">
          <div className="flex items-center gap-2 px-6 py-4 border-b">
            <Hammer className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">Assigned Karigar</h2>
          </div>
          <div className="p-6">
            {p.karigar_name ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Karigar</p>
                  <button data-testid="product-karigar-link" className="font-medium text-sm text-amber-500 hover:underline" onClick={() => p.karigar_id && navigate(`/karigars/${p.karigar_id}`)}>
                    {p.karigar_name}
                  </button>
                </div>
                <Field label="Karigar Cost" value={fmt(p.karigar_cost)} />
                <Field label="Sent" value={fmtDate(p.sent_date)} />
                <Field label="Expected Return" value={fmtDate(p.expected_return)} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No karigar assigned yet.</p>
            )}
            {p.remarks && (
              <div className="mt-5 pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-1">Notes / Measurements</p>
                <p className="text-sm">{p.remarks}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-card" data-testid="product-timeline">
        <div className="flex items-center gap-2 px-6 py-4 border-b">
          <History className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-heading font-semibold">Status Timeline</h2>
        </div>
        {timeline.length === 0 ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">No events recorded yet.</p>
        ) : (
          <ul className="px-6 py-4">
            {timeline.map((ev, i) => {
              const dot = {
                "Payment Received": "bg-green-500", "Sent To Karigar": "bg-orange-500",
                "Order Created": "bg-blue-500", "Cancelled": "bg-red-500",
                "Received From Karigar": "bg-teal-500", "Delivered": "bg-green-500",
                "PDF Uploaded": "bg-violet-500", "PDF Removed": "bg-zinc-500",
              }[ev.event] || "bg-amber-500";
              const dt = ev.created_at ? new Date(ev.created_at) : null;
              return (
                <li key={ev.id || i} className="relative pl-6 pb-5 last:pb-1">
                  {i < timeline.length - 1 && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-border" />}
                  <span className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ${dot}`} />
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <p className="text-sm font-medium">{ev.event}</p>
                    {ev.details && <p className="text-xs text-muted-foreground">{ev.details}</p>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {dt && !isNaN(dt) ? dt.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ev.created_at}
                    {ev.user && ` · ${ev.user}`}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
